import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useAtivos, useSubstituir } from '../hooks';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  assetId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReplaceModal({ open, assetId, onClose, onSuccess }: Props) {
  const mutation = useSubstituir(assetId);
  // Só ativos AVAILABLE são candidatos a substituto.
  const availableQuery = useAtivos({ status: 'available', limit: 100 });
  const [replacementAssetId, setReplacementAssetId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setReplacementAssetId(''); setReason(''); setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!replacementAssetId) { setError('Selecione o ativo substituto'); return; }
    if (!reason.trim()) { setError('Informe o motivo'); return; }
    if (replacementAssetId === assetId) { setError('Substituto não pode ser o próprio ativo'); return; }
    try {
      await mutation.mutateAsync({ replacementAssetId, reason: reason.trim() });
      reset(); onSuccess(); onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao substituir ativo'));
    }
  }

  const options = (availableQuery.data?.data ?? []).filter((a) => a.id !== assetId);

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">Substituir ativo</Dialog.Title>
          <p className="mt-1 text-xs text-gray-500">
            Fecha o vínculo atual e abre um novo com o mesmo responsável no ativo substituto — em uma única transação.
          </p>
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <div>
              <AutocompleteSelect
                label="Ativo substituto (apenas disponíveis) *"
                options={[
                  { value: '', label: 'Selecione...' },
                  ...options.map((a) => ({
                    value: a.id,
                    label: `${a.displayCode ?? a.assetCode} — ${a.description}`,
                  })),
                ]}
                value={replacementAssetId}
                onChange={setReplacementAssetId}
              />
              {options.length === 0 && (
                <span className="mt-1 block text-xs text-gray-500">Nenhum ativo disponível para substituição.</span>
              )}
            </div>
            <Textarea
              label="Motivo *"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />

            {error && <Alert variant="error">{error}</Alert>}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
              <Button
                type="submit"
                loading={mutation.isPending}
                className="!bg-indigo-600 hover:!bg-indigo-700 focus:!ring-indigo-500"
              >
                {mutation.isPending ? 'Substituindo…' : 'Confirmar substituição'}
              </Button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
